/**
 * Admin console: report queue, curation (feature/schedule/unfeature/remove),
 * user management (roles, ban/unban), and the full audit log. The menu entry
 * only appears for role=admin, and every endpoint re-checks the role
 * server-side — this panel is convenience, not the security boundary.
 */
import { OnlineClient, type LevelSummary } from '../online/client';
import { t } from './strings';

const esc = (s: string): string => s.replace(/[&<>"']/g, (ch) => `&#${ch.charCodeAt(0)};`);

export class AdminUi {
  private levelSearch = '';
  private userSearch = '';

  constructor(
    private readonly root: HTMLElement,
    private readonly client: OnlineClient,
    private readonly onClose: () => void,
  ) {}

  async show(): Promise<void> {
    this.root.innerHTML = `
      <h2>${t('admin.title')}</h2>
      <div class="ed-cat">${t('admin.reports')}</div>
      <div id="ad-reports">…</div>
      <div class="ed-cat">${t('admin.curation')}</div>
      <div id="ad-curation">…</div>
      <div class="ed-cat">${t('admin.users')}</div>
      <div id="ad-users">…</div>
      <div class="ed-cat">${t('admin.audit')}</div>
      <div id="ad-audit">…</div>
      <button class="small" id="ad-close">${t('online.close')}</button>
    `;
    this.root.querySelector('#ad-close')?.addEventListener('click', () => this.onClose());
    await Promise.all([
      this.renderReports(),
      this.renderCuration(),
      this.renderUsers(),
      this.renderAudit(),
    ]);
  }

  // --- Reports ---------------------------------------------------------------------

  private async renderReports(): Promise<void> {
    const host = this.root.querySelector('#ad-reports');
    if (!host) return;
    const reports = await this.client.modReports();
    if (!reports.ok) {
      host.innerHTML = `<span class="hint">${esc(reports.error)}</span>`;
      return;
    }
    host.innerHTML =
      reports.value
        .map(
          (r) => `<div class="ach admin-row" data-report="${r.id}">
            <b>#${r.id} ${esc(r.subjectType)}:${r.subjectId}</b>
            <span>${esc(r.reason)} — ${esc(r.reporter)}</span>
            <div class="card-actions">
              <input type="text" maxlength="500" class="mod-reason" placeholder="${t('online.mod.reason-ph')}">
              <button class="small" data-resolve="${r.id}">${t('online.mod.resolve')}</button>
              ${
                r.subjectType === 'level'
                  ? `<button class="small danger" data-rmlevel="${r.subjectId}" data-from="${r.id}">${t('online.mod.remove-level')}</button>`
                  : ''
              }
              ${
                r.subjectType === 'comment'
                  ? `<button class="small danger" data-rmcomment="${r.subjectId}">${t('online.mod.remove-comment')}</button>`
                  : ''
              }
            </div></div>`,
        )
        .join('') || `<span class="hint">${t('admin.no-results')}</span>`;
    const reason = (id: number): string =>
      host.querySelector<HTMLInputElement>(`[data-report="${id}"] .mod-reason`)?.value.trim() ||
      'resolved';
    for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-resolve]')) {
      btn.addEventListener('click', () => {
        const id = Number(btn.dataset.resolve);
        void this.client.modResolve(id, reason(id)).then(() => this.renderReports());
      });
    }
    for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-rmlevel]')) {
      btn.addEventListener('click', () => {
        void this.client
          .modRemoveLevel(Number(btn.dataset.rmlevel), reason(Number(btn.dataset.from)))
          .then(() => Promise.all([this.renderReports(), this.renderCuration()]));
      });
    }
    for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-rmcomment]')) {
      btn.addEventListener('click', () => {
        void this.client
          .modRemoveComment(Number(btn.dataset.rmcomment))
          .then(() => this.renderReports());
      });
    }
  }

  // --- Curation --------------------------------------------------------------------

  private async renderCuration(): Promise<void> {
    const host = this.root.querySelector('#ad-curation');
    if (!host) return;
    const [showcase, browse] = await Promise.all([
      this.client.showcase(),
      this.levelSearch
        ? this.client.browse({ search: this.levelSearch, sort: 'recent' })
        : this.client.browse({ sort: 'recent' }),
    ]);

    const levelRow = (l: LevelSummary, badge = '', featured = false): string => `
      <div class="ach admin-row">
        <b>${badge}${esc(l.name)} <span class="hint">by ${esc(l.author)}</span></b>
        <div class="card-actions">
          <button class="small" data-feature="${l.id}" data-on="${featured ? '0' : '1'}">
            ${featured ? t('admin.unfeature') : t('online.mod.feature')}</button>
          <button class="small" data-schedule="${l.id}">${t('online.mod.schedule')}</button>
          <button class="small danger" data-rml="${l.id}">${t('admin.remove')}</button>
        </div></div>`;

    let html = '';
    if (showcase.ok) {
      if (showcase.value.daily)
        html += levelRow(showcase.value.daily, `☀ ${t('admin.curation.daily')}: `);
      if (showcase.value.weekly)
        html += levelRow(showcase.value.weekly, `◈ ${t('admin.curation.weekly')}: `);
      html += showcase.value.featured.map((l) => levelRow(l, '★ ', true)).join('');
    }
    html += `
      <div class="ed-row">
        <input id="ad-level-search" type="search" placeholder="${t('admin.curation.search')}"
          value="${esc(this.levelSearch)}">
      </div>`;
    if (browse.ok) {
      const featuredIds = new Set(showcase.ok ? showcase.value.featured.map((f) => f.id) : []);
      html += browse.value.levels
        .filter((l) => !featuredIds.has(l.id))
        .slice(0, 8)
        .map((l) => levelRow(l))
        .join('');
    }
    host.innerHTML = html || `<span class="hint">${t('admin.no-results')}</span>`;

    const search = host.querySelector<HTMLInputElement>('#ad-level-search');
    search?.addEventListener('change', () => {
      this.levelSearch = search.value;
      void this.renderCuration();
    });
    for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-feature]')) {
      btn.addEventListener('click', () => {
        void this.client
          .modFeature(Number(btn.dataset.feature), btn.dataset.on === '1')
          .then(() => this.renderCuration());
      });
    }
    for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-schedule]')) {
      btn.addEventListener('click', () => {
        const date = prompt(t('online.mod.schedule-ph'), new Date().toISOString().slice(0, 10));
        if (!date) return;
        const weekly = /^\d{4}-W\d{2}$/.test(date);
        void this.client
          .modSchedule(Number(btn.dataset.schedule), weekly ? null : date, weekly ? date : null)
          .then(() => this.renderCuration());
      });
    }
    for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-rml]')) {
      btn.addEventListener('click', () => {
        const reason = prompt(t('online.mod.reason-ph'));
        if (!reason) return;
        void this.client
          .modRemoveLevel(Number(btn.dataset.rml), reason)
          .then(() => this.renderCuration());
      });
    }
  }

  // --- Users -----------------------------------------------------------------------

  private async renderUsers(): Promise<void> {
    const host = this.root.querySelector('#ad-users');
    if (!host) return;
    const users = await this.client.modUsers(this.userSearch);
    const me = this.client.user;
    let rows = '';
    if (users.ok) {
      rows = users.value
        .map((u) => {
          const self = me !== null && u.id === me.id;
          const roleSelect = self
            ? `<span class="hint">${esc(u.role)} ${t('admin.self')}</span>`
            : `<select data-role="${u.id}">
                ${['player', 'moderator', 'admin']
                  .map((r) => `<option ${r === u.role ? 'selected' : ''}>${r}</option>`)
                  .join('')}
              </select>`;
          const banControls = self
            ? ''
            : u.bannedAt
              ? `<button class="small" data-unban="${u.id}">${t('admin.unban')}</button>`
              : `<input type="text" maxlength="500" class="mod-reason" placeholder="${t('admin.ban-reason')}">
                 <button class="small danger" data-ban="${u.id}">${t('admin.ban')}</button>`;
          const deleteBtn = self
            ? ''
            : `<button class="small danger" data-del="${u.id}" data-handle="${esc(u.handle)}">${t('admin.delete')}</button>`;
          return `<div class="ach admin-row" data-user="${u.id}">
            <b>${esc(u.handle)}${u.bannedAt ? ` <span class="hint">(${t('admin.banned')})</span>` : ''}</b>
            <span class="hint">${t('admin.joined', { date: u.createdAt.slice(0, 10) })}</span>
            <div class="card-actions">${roleSelect}${banControls}${deleteBtn}</div>
          </div>`;
        })
        .join('');
    }
    host.innerHTML = `
      <div class="ed-row">
        <input id="ad-user-search" type="search" placeholder="${t('admin.users.search')}"
          value="${esc(this.userSearch)}">
      </div>
      ${rows || `<span class="hint">${t('admin.no-results')}</span>`}`;

    const search = host.querySelector<HTMLInputElement>('#ad-user-search');
    search?.addEventListener('change', () => {
      this.userSearch = search.value;
      void this.renderUsers();
    });
    for (const sel of host.querySelectorAll<HTMLSelectElement>('[data-role]')) {
      sel.addEventListener('change', () => {
        void this.client
          .modSetRole(Number(sel.dataset.role), sel.value)
          .then(() => Promise.all([this.renderUsers(), this.renderAudit()]));
      });
    }
    for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-ban]')) {
      btn.addEventListener('click', () => {
        const id = Number(btn.dataset.ban);
        const reason =
          host.querySelector<HTMLInputElement>(`[data-user="${id}"] .mod-reason`)?.value.trim() ||
          'banned by admin';
        void this.client
          .modBan(id, reason)
          .then(() => Promise.all([this.renderUsers(), this.renderAudit()]));
      });
    }
    for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-unban]')) {
      btn.addEventListener('click', () => {
        void this.client
          .modUnban(Number(btn.dataset.unban))
          .then(() => Promise.all([this.renderUsers(), this.renderAudit()]));
      });
    }
    for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-del]')) {
      btn.addEventListener('click', () => {
        // Irreversible: everything they made goes with the account.
        if (!confirm(t('admin.delete-confirm', { handle: btn.dataset.handle ?? '' }))) return;
        void this.client
          .modDeleteUser(Number(btn.dataset.del))
          .then(() => Promise.all([this.renderUsers(), this.renderCuration(), this.renderAudit()]));
      });
    }
  }

  // --- Audit log -------------------------------------------------------------------

  private async renderAudit(): Promise<void> {
    const host = this.root.querySelector('#ad-audit');
    if (!host) return;
    const audit = await this.client.modAudit();
    if (!audit.ok) {
      host.innerHTML = `<span class="hint">${esc(audit.error)}</span>`;
      return;
    }
    host.innerHTML =
      `<div class="ach-list admin-audit">` +
      (audit.value
        .map(
          (e) => `<div class="stat-line">
            <span><b>${esc(e.action)}</b> ${esc(e.subject)}${e.detail ? ` — ${esc(e.detail)}` : ''}</span>
            <b class="hint">u${e.actorId} · ${e.createdAt.slice(0, 16).replace('T', ' ')}</b>
          </div>`,
        )
        .join('') || `<span class="hint">${t('admin.no-results')}</span>`) +
      `</div>`;
  }
}
